import { ForwardedRef, forwardRef, useCallback, useMemo } from "react";
import { GroupBase, SelectInstance } from "react-select";
import CreatableSelect, { CreatableProps } from "react-select/creatable";
import useLatestRef from "utils/useLatestRef";
import useSelectStyles from "utils/useSelectStyles";

type Option = { value: string };

function getOptionLabel(option: Option) {
  return option.value;
}

type SimpleCreatableSelectProps = Omit<
  CreatableProps<Option, true, GroupBase<Option>>,
  "options" | "value" | "onChange"
> & {
  value?: string[];
  onChange?: (value: string[]) => void;
};

function SimpleCreatableSelect(
  { onChange, styles, value: rawValue, ...rest }: SimpleCreatableSelectProps,
  ref: ForwardedRef<SelectInstance<Option, true>>,
) {
  const styleProps = useSelectStyles(styles);

  const valueOptions = useMemo(
    () => rawValue?.map((value) => ({ value })),
    [rawValue],
  );

  const latestOnChangeRef = useLatestRef(onChange);

  const handleChange = useCallback(
    (options: readonly Option[] | null) => {
      if (options) {
        latestOnChangeRef.current?.(options.map((option) => option.value));
      } else {
        latestOnChangeRef.current?.([]);
      }
    },
    [latestOnChangeRef],
  );

  return (
    <CreatableSelect<Option, true>
      {...styleProps}
      {...rest}
      ref={ref}
      isMulti
      getOptionLabel={getOptionLabel}
      value={valueOptions}
      onChange={handleChange}
    />
  );
}

export default forwardRef(SimpleCreatableSelect);
